// Modal layers of the shell: the news preview, the side sheet, the Orca panel
// and the drawer. Every open layer is on one stack, so there is always one
// clear top layer:
// • Esc and a click on the dimmed page close only the top layer;
// • focus returns to the element that opened a layer when it closes;
// • the page under any open layer doesn't scroll;
// • only ONE scrim is ever visible — the lowest layer's. Orca opened from a
//   detail view (or a sheet opened over the news) reuses the scrim below it
//   (CSS: 04-ask-bar / 18-mock-ux); the global Orca bar hides while a detail
//   view is open, because the detail has its own «Спросить Orca».
import { useEffect, useRef, useSyncExternalStore } from 'react';

export type LayerId = 'news' | 'sheet' | 'orca' | 'drawer';
type Layer = { id: LayerId; close: () => void };
let stack: Layer[] = [];
const subs = new Set<() => void>();
const emit = () => {
  if (typeof document !== 'undefined') document.documentElement.classList.toggle('lock-scroll', stack.length > 0);
  subs.forEach((f) => f());
};
const subscribe = (f: () => void) => { subs.add(f); return () => { subs.delete(f); }; };

export const topLayer = (): LayerId | undefined => stack[stack.length - 1]?.id;
/** closes the top layer (Esc, a click on the scrim) */
export const closeTopLayer = () => stack[stack.length - 1]?.close();
/** a click inside a lower layer brings it back: the layers above it close */
export function closeAbove(id: LayerId) {
  const i = stack.findIndex((l) => l.id === id);
  if (i >= 0) [...stack.slice(i + 1)].reverse().forEach((l) => l.close());
}

if (typeof window !== 'undefined') {
  window.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape' || !stack.length) return;
    e.preventDefault();
    closeTopLayer();
  });
}

/** Registers an open layer; returns whether it is the top one. */
export function useLayer(id: LayerId, open: boolean, close: () => void) {
  const closeRef = useRef(close);
  closeRef.current = close;
  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    stack = [...stack.filter((x) => x.id !== id), { id, close: () => closeRef.current() }];
    emit();
    return () => {
      stack = stack.filter((x) => x.id !== id);
      emit();
      // the element that opened the layer gets the focus back
      if (opener?.isConnected && !opener.closest('[inert]')) opener.focus({ preventScroll: true });
    };
  }, [id, open]);
  return useSyncExternalStore(subscribe, () => topLayer() === id, () => false);
}

const hasDetail = () => stack.some((l) => l.id === 'news' || l.id === 'sheet');
/** a detail view (news preview or side sheet) is open */
export const useDetailOpen = () => useSyncExternalStore(subscribe, hasDetail, () => false);
/** DEV/tests: the layer stack, bottom → top */
export const layerStack = () => stack.map((l) => l.id);
if (import.meta.env.DEV && typeof window !== 'undefined') (window as unknown as Record<string, unknown>).__orcaLayers = layerStack;
